import { Cell, Row, Workbook, Worksheet } from 'exceljs';
import { saveAs } from 'file-saver';
import { get, isNumber, set } from 'lodash';
import { getCurrentDayString } from '@/helpers';
import { AssessmentPeriodsExportData, AssessmentPeriodsExportSummary } from '@/models';
import { STYLES, NA, Style, CallbackFn } from './project-exporting-config';
import { addTableHeaderRow } from './project-exporting-helpers';

export default class AssessmentPeriodsExport {
  private workbook = new Workbook();
  private sWs: Worksheet;
  private apWs: Worksheet;
  private exportData: AssessmentPeriodsExportData[];
  private sColumnWidths = [12, 15, 15, 15, 15];
  private apColumnWidths = [10, 30, 30, 20, 30, 30, 12, 8, 12, 16, 12];
  private summaryData: AssessmentPeriodsExportSummary[];

  constructor(exportData: AssessmentPeriodsExportData[], summaryData: AssessmentPeriodsExportSummary[]) {
    this.exportData = exportData;
    this.summaryData = summaryData;
    this.sWs = this.workbook.addWorksheet('Summary');
    this.apWs = this.workbook.addWorksheet('Assessment Period');
  }

  addStyleToCell(cell: Cell, style: Style = {}): void {
    Object.keys(style).map(key => set(cell, key, get(style, key)));
  }

  get exportFileName(): string {
    return `HIVE_Assessment_Periods_Result_${getCurrentDayString()}.xlsx`;
  }

  addRow(ws: Worksheet, data: unknown[], style?: Style, callback?: CallbackFn): Row {
    const row = ws.addRow(data);
    row.eachCell((cell, index) => {
      if (typeof cell.value === 'number') {
        cell.alignment = STYLES.middleRightWrappedAlignment;
      } else {
        cell.alignment = STYLES.middleLeftWrappedAlignment;
      }
      if (cell.value === NA) {
        cell.font = STYLES.naFont;
        cell.alignment = STYLES.middleRightWrappedAlignment;
      }
      this.addStyleToCell(cell, style);
      if (callback) {
        callback(cell, index);
      }
    });
    return row;
  }

  interleaveRowStyle(ws: Worksheet, data: unknown[], index: number, callbackFn?: CallbackFn): Row {
    return index % 2 === 0
      ? this.addRow(ws, data, { fill: STYLES.lightTealFill }, callbackFn)
      : this.addRow(ws, data, {}, callbackFn);
  }

  formatDisplayNumber(num: string): string {
    return isNumber(Number(num)) ? num : NA;
  }

  buildRowInformation(exportData: AssessmentPeriodsExportData): unknown[] {
    const {
      projectId,
      projectName,
      projectType,
      sponsor,
      manager,
      hours,
      key,
      projectDrops,
      evaluationStatus,
      quarter,
      numOfContributors
    } = exportData;
    return [
      projectId,
      projectName,
      projectType,
      numOfContributors,
      sponsor,
      manager,
      this.formatDisplayNumber(hours?.toString()),
      this.formatDisplayNumber(key?.toString()),
      this.formatDisplayNumber(projectDrops?.toString()),
      evaluationStatus,
      quarter
    ];
  }

  addInformationToWorksheet(): void {
    const header = [
      { title: 'Project ID' },
      { title: 'Project name' },
      { title: 'Project Type', style: { fill: STYLES.lightOrangeFill } },
      { title: 'Num of Contributors', style: { fill: STYLES.lightOrangeFill } },
      { title: 'Sponsor' },
      { title: 'Project Manager' },
      { title: 'Hours', style: { fill: STYLES.lightOrangeFill } },
      { title: 'Pollens', style: { fill: STYLES.lightOrangeFill } },
      { title: 'Project Pollens' },
      { title: 'Evaluation Status' },
      { title: 'Quarter', style: { fill: STYLES.lightOrangeFill } }
    ];
    addTableHeaderRow(this.apWs, header);
    const boldColumnIdxs = [7, 8, 9];
    const rows = this.exportData.map(d => this.buildRowInformation(d));

    rows.forEach((rowData, rowIdx) => {
      this.interleaveRowStyle(this.apWs, rowData, rowIdx, (cell, index) => {
        if (boldColumnIdxs.includes(index)) {
          if (cell.value !== NA) {
            cell.font = STYLES.boldFont;
          }
        }
      });
    });
  }

  setColumnWidth(): void {
    this.sWs.columns.forEach((column, index) => {
      column.width = this.sColumnWidths[index];
    });
    this.apWs.columns.forEach((column, index) => {
      column.width = this.apColumnWidths[index];
    });
  }

  addBorder(): void {
    this.apWs.eachRow({ includeEmpty: true }, row => {
      row.eachCell({ includeEmpty: true }, cell => {
        const style = STYLES.thinGreyBorder;
        cell.border = {
          top: cell?.border?.top || style,
          bottom: cell?.border?.bottom || style,
          left: cell?.border?.left || style,
          right: cell?.border?.right || style
        };
      });
    });
  }

  addEmptyRow(ws: Worksheet): void {
    ws.addRow([]);
  }

  addSummaryDataToWorkSheet(): void {
    const ws = this.sWs;
    this.addRow(ws, ['Quarter', 'Contributors', 'Hours', 'Drops'], {
      font: STYLES.whiteBoldFont,
      fill: STYLES.lightOrangeFill
    });
    this.summaryData.map(({ quarter, numOfContributors, totalHours, totalDrops }) => {
      this.addRow(
        ws,
        [quarter, numOfContributors, totalHours, totalDrops],
        quarter === 'Total'
          ? {
              font: STYLES.boldFont,
              fill: STYLES.veryLightOrangeFill
            }
          : undefined
      );
    });
  }

  async generateXLSXFile(): Promise<void> {
    this.addSummaryDataToWorkSheet();
    this.addInformationToWorksheet();
    this.setColumnWidth();
    this.addBorder();

    try {
      const buf = await this.workbook.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, this.exportFileName);
    } catch (error) {
      console.log(error);
    }
  }
}
